/**
 * Batch Status Component
 * Monitor status of batch template processing
 */

import React, { useState, useEffect } from 'react';
import { templatesAPI } from '../../services/api';

interface BatchInfo {
  batchId: string;
  totalCount: number;
  processedCount: number;
  templatesCreated: number;
  errors: number;
  uploadedAt: string;
  status: 'processing' | 'completed' | 'failed';
}

interface SampleInfo {
  id: string;
  filename: string;
  processed: boolean;
  error?: string;
  templateId?: string;
  detectedBankName?: string;
}

interface BatchStatusProps {
  batchId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const BatchStatus: React.FC<BatchStatusProps> = ({
  batchId,
  autoRefresh = true,
  refreshInterval = 3000
}) => {
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [samples, setSamples] = useState<SampleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBatchStatus();

    if (autoRefresh) {
      const interval = setInterval(loadBatchStatus, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [batchId, autoRefresh, refreshInterval]);

  const loadBatchStatus = async () => {
    try {
      setError(null);
      const response = await templatesAPI.getBatchStatus(batchId);

      setBatchInfo({
        batchId: response.batchId,
        totalCount: response.totalCount,
        processedCount: response.processedCount,
        templatesCreated: response.templatesCreated,
        errors: response.errors,
        uploadedAt: response.uploadedAt,
        status:
          response.processedCount === response.totalCount
            ? response.errors === response.totalCount
              ? 'failed'
              : 'completed'
            : 'processing'
      });

      if (response.samples) {
        setSamples(response.samples);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load batch status');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !batchInfo) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !batchInfo) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadBatchStatus}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!batchInfo) return null;

  const progressPercent = (batchInfo.processedCount / batchInfo.totalCount) * 100;

  return (
    <div className="space-y-6">
      {/* Batch Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Batch Processing</h3>
          {batchInfo.status === 'processing' && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
              <span>Processing...</span>
            </div>
          )}
          {batchInfo.status === 'completed' && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Completed</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">
              {batchInfo.processedCount} / {batchInfo.totalCount}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${
                batchInfo.status === 'completed'
                  ? 'bg-green-600'
                  : batchInfo.status === 'failed'
                  ? 'bg-red-600'
                  : 'bg-blue-600'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {batchInfo.totalCount}
            </div>
            <div className="text-xs text-gray-600 mt-1">Total Files</div>
          </div>

          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {batchInfo.templatesCreated}
            </div>
            <div className="text-xs text-green-700 mt-1">Templates Created</div>
          </div>

          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {batchInfo.errors}
            </div>
            <div className="text-xs text-red-700 mt-1">Errors</div>
          </div>
        </div>

        {/* Batch Info */}
        <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
          <p>
            <span className="font-medium">Batch ID:</span> {batchInfo.batchId}
          </p>
          <p className="mt-1">
            <span className="font-medium">Uploaded:</span>{' '}
            {new Date(batchInfo.uploadedAt).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Sample Files */}
      {samples.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Files</h3>
          </div>

          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {samples.map((sample) => (
              <div key={sample.id} className="px-6 py-4 flex items-center gap-4">
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {sample.processed && !sample.error ? (
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : sample.error ? (
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {sample.filename}
                  </p>
                  {sample.detectedBankName && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Detected: {sample.detectedBankName}
                    </p>
                  )}
                  {sample.error && (
                    <p className="text-xs text-red-600 mt-0.5">{sample.error}</p>
                  )}
                </div>

                {/* Template Link */}
                {sample.templateId && (
                  <div className="flex-shrink-0">
                    <a
                      href={`/templates/${sample.templateId}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View Template
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Batch List Component
 * Display list of recent batch uploads
 */
export const BatchList: React.FC = () => {
  const [batches, setBatches] = useState<BatchInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // This would call an API endpoint to list recent batches
    // For now, it's a placeholder
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No recent batch uploads
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {batches.map((batch) => (
        <div key={batch.batchId} className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Batch {batch.batchId.slice(0, 8)}</p>
              <p className="text-sm text-gray-600">
                {batch.processedCount} / {batch.totalCount} processed
              </p>
            </div>
            <button className="text-blue-600 hover:text-blue-800 text-sm">
              View Details
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
